import { FileText, Globe, Wand2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { isCorsBlockedBadge } from './recipe-text-input-utils'

interface RecipeTextInputProps {
  activeTab: 'text' | 'url'
  inputText: string
  urlInput: string
  isLoading: boolean
  onTabChange: (tab: 'text' | 'url') => void
  onInputTextChange: (value: string) => void
  onUrlInputChange: (value: string) => void
  onParse: () => Promise<void>
  onLoadExample: () => void
  onClear: () => void
}

export function RecipeTextInput({
  activeTab,
  inputText,
  urlInput,
  isLoading,
  onTabChange,
  onInputTextChange,
  onUrlInputChange,
  onParse,
  onLoadExample,
  onClear,
}: RecipeTextInputProps) {
  return (
    <>
      <div className="-mx-6 overflow-x-auto border-b border-gray-200 px-6">
        <div className="flex min-w-max">
          <button
            onClick={() => onTabChange('text')}
            className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'text'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText className="mr-2 inline h-4 w-4" />
            Tekst plakken
          </button>
          <button
            onClick={() => onTabChange('url')}
            className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'url'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Globe className="mr-2 inline h-4 w-4" />
            Link gebruiken
          </button>
        </div>
      </div>

      {activeTab === 'text' ? (
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">Recepttekst</label>
          <Textarea
            value={inputText}
            onChange={(event) => onInputTextChange(event.target.value)}
            placeholder="Plak hier het recept zoals je het hebt gevonden. Bijvoorbeeld titel, ingrediënten en stappen."
            rows={12}
            className="w-full"
          />
          <div className="mt-2 flex items-center justify-between">
            <p className="text-xs text-gray-500">{inputText.length} tekens</p>
            <Button variant="ghost" size="sm" onClick={onLoadExample} className="text-xs">
              <FileText className="mr-1 h-3 w-3" />
              Voorbeeld laden
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">Receptlink</label>
          <div className="relative">
            <Input
              type="url"
              value={urlInput}
              onChange={(event) => onUrlInputChange(event.target.value)}
              placeholder="https://voorbeeld.nl/recept"
              className="w-full pr-10"
            />
            {urlInput ? <div className="absolute right-3 top-1/2 -translate-y-1/2 transform">{isCorsBlockedBadge(urlInput)}</div> : null}
          </div>
          <div className="mt-3 space-y-2">
            <p className="text-xs text-gray-500">
              Plak hier de link naar een receptpagina. Soms blokkeert een website dit. In dat geval kun je altijd terug naar &quot;Tekst plakken&quot;.
            </p>
            <div className="rounded-lg border border-green-200 bg-green-50 p-3">
              <p className="mb-1 text-xs font-medium text-green-700">Vaak bruikbaar:</p>
              <div className="space-y-0.5 text-xs text-green-600">
                <div>• allerhande.nl</div>
                <div>• leukerecepten.nl</div>
                <div>• food.com</div>
                <div>• allrecipes.com</div>
                <div>• bbcgoodfood.com</div>
              </div>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="mb-1 text-xs font-medium text-amber-700">Soms minder bruikbaar:</p>
              <div className="space-y-0.5 text-xs text-amber-600">
                <div>• Websites met strenge beveiliging</div>
                <div>• Sommige commerciële foodblogs</div>
                <div>• Pagina&apos;s die veel achter scripts verstoppen</div>
              </div>
              <p className="mt-2 text-xs text-amber-600">
                Tip: lukt een link niet, kopieer dan gewoon de tekst van het recept en plak die hier in.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          onClick={() => void onParse()}
          disabled={isLoading || (activeTab === 'text' ? !inputText.trim() : !urlInput.trim())}
          className="flex-1"
        >
          {isLoading ? (
            <>
              <Wand2 className="mr-2 h-4 w-4 animate-spin" />
              {activeTab === 'url' ? 'Recept ophalen...' : 'Recept verwerken...'}
            </>
          ) : (
            <>
              {activeTab === 'url' ? <Globe className="mr-2 h-4 w-4" /> : <Wand2 className="mr-2 h-4 w-4" />}
              {activeTab === 'url' ? 'Link gebruiken' : 'Tekst verwerken'}
            </>
          )}
        </Button>

        {inputText || urlInput ? (
          <Button variant="outline" onClick={onClear}>
            Wissen
          </Button>
        ) : null}
      </div>
    </>
  )
}
